import React, { useRef } from 'react';
import { Box, Container, DataTable, Flex, Grid, Spinner, TableState } from '@tq/ui-components';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { DataTablePagination, EmptyDataCard } from '../../../components';
import { ReportData } from 'types';
import {
  exceptionLogsDetailsModalState,
  exceptionLogsOrderByColumnState,
  exceptionLogsOrderByDescendingState,
  exceptionLogsPageNumberState,
  exceptionLogsPageSizeState,
  exceptionLogsSortByState,
} from 'store';
import { useExceptionLogs } from 'api/trafficMonitor/hooks/useExceptionLogs';
import { useExceptionLogsColumns, useExceptionLogsFilters } from '../hooks';
import { ExceptionLogsMonitorFilters } from './ExceptionLogsMonitorFilters';
import { ExceptionLogMonitorDetails } from './ExceptionLogMonitorDetails';

export const ExceptionLogsMonitorTable: React.FC = () => {
  const { t } = useTranslation('traffic_monitor');

  const { data: exceptionsRequestData, isLoading } = useExceptionLogs();
  const filters = useExceptionLogsFilters();
  const columns = useExceptionLogsColumns();
  const tableRef = useRef(null);
  const sortBy = useRecoilValue(exceptionLogsSortByState);
  const setOrderByColumn = useSetRecoilState(exceptionLogsOrderByColumnState);
  const setOrderByDescending = useSetRecoilState(exceptionLogsOrderByDescendingState);
  const isModalOpen = useRecoilValue(exceptionLogsDetailsModalState);

  const handleStateChange = (state: TableState<ReportData>) => {
    const newOrderByColumn = state.sortBy?.[0]?.id;
    const newOrderByDescending = state.sortBy?.[0]?.desc ?? false;

    if (newOrderByColumn) {
      setOrderByColumn(newOrderByColumn);
      setOrderByDescending(newOrderByDescending);
    }
  };

  return (
    <>
      <Container px={{ base: 0, lg: 4, xl: 0 }} mb={6}>
        <Grid gridGap={4}>
          <Box>
            <Grid gridGap={4}>
              <ExceptionLogsMonitorFilters filters={filters} />
            </Grid>
          </Box>
          {isLoading ? (
            <Flex justifyContent='center'>
              <Spinner />
            </Flex>
          ) : !exceptionsRequestData?.exceptions?.items.length ? (
            <EmptyDataCard message={t('no_data', 'No results found')} />
          ) : (
            <>
              <Box ref={tableRef} overflow='hidden'>
                <DataTable
                  columns={columns}
                  data={exceptionsRequestData?.exceptions?.items}
                  disableSortRemove
                  disablePagination
                  enableOverflowScrollingContainer
                  onStateChange={handleStateChange}
                  size='sm'
                  sortBy={sortBy}
                  sx={{
                    '.tqc-datatable__th-content': {
                      minWidth: '130px',
                    },
                  }}
                  whiteSpace='normal'
                />
              </Box>
              <Box>
                <DataTablePagination
                  paginationInfo={exceptionsRequestData?.paginationInfo}
                  pageNumberState={exceptionLogsPageNumberState}
                  pageSizeState={exceptionLogsPageSizeState}
                  tableRef={tableRef}
                  tableScrollHeaderOffset={167}
                />
              </Box>
            </>
          )}
        </Grid>
      </Container>
      {isModalOpen && <ExceptionLogMonitorDetails />}
    </>
  );
};
